import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Loader } from "lucide-react";
import useCampuses from "../hooks/useCampuses";
import useGrades from "../hooks/useGrades";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;
const GRADES = ["Nursery", "LKG", "UKG", "Grade I", "Grade II", "Grade III", "Grade IV", "Grade V",
  "Grade VI", "Grade VII", "Grade VIII", "Grade IX", "Grade X", "Grade XI", "Grade XII"];

const LeadPopup = () => {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ parentName: "", childName: "", phone: "", campus: "", campusId: "", grade: "" });
  const campusList = useCampuses();
  const campusNames = campusList.map((c) => c.name);
  const gradeList = useGrades(form.campusId);

  useEffect(() => {
    // Show popup on every page load after a 5-second delay
    const t = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parentName || !form.phone) return;
    setLoading(true);
    try {
      await axios.post(`${CRM}/api/public/leads`, {
        parent_name: form.parentName,
        child_name: form.childName || undefined,
        phone: form.phone,
        campus_preference: form.campus || undefined,
        grade_applying: form.grade || undefined,
        source: "Website",
        city: "Bengaluru",
        school: "TIPS Bengaluru",
      }, { headers: { "X-API-Key": CRM_KEY } });
      setSubmitted(true);
      setTimeout(dismiss, 2500);
    } catch {
      // silent — show success anyway
      setSubmitted(true);
      setTimeout(dismiss, 2500);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(10,0,0,0.65)", backdropFilter: "blur(4px)" }}
      data-testid="lead-popup-overlay"
    >
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-visible"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-popup-modal"
      >
        {/* Red header banner */}
        <div className="bg-[#9B1522] px-6 pt-6 pb-5 text-center rounded-t-2xl overflow-hidden">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            data-testid="lead-popup-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <div className="w-14 h-14 rounded-sm overflow-hidden mx-auto mb-3 border border-white/30 bg-white">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/fcc543bd-d42f-4ba5-bf4e-a8bc67b4a41b/images/de05625decb0b8b65cf662a6ed27ada0d7f575706f16a0d41c94b221dd2dac5e.jpeg"
              alt="TIPS"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.parentElement.innerHTML = '<span class="text-[#9B1522] text-xs font-black flex items-center justify-center h-full text-center leading-tight p-1">THE<br/>INDIAN<br/>PUBLIC<br/>SCHOOL</span>'; }}
            />
          </div>
          <h2 className="text-white text-lg font-bold leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Admissions Open 2026–27
          </h2>
          <p className="text-[#FFC8CE] text-xs mt-1">Nursery to Grade XII · CBSE · Bengaluru</p>
        </div>

        {/* Form */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-green-600">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="font-bold text-gray-800">Thank you!</p>
              <p className="text-sm text-gray-500 mt-1">Our admissions team will contact you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-popup-form">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Parent / Guardian Name *</label>
                <input
                  required
                  type="text"
                  value={form.parentName}
                  onChange={(e) => setForm({ ...form, parentName: e.target.value })}
                  placeholder="Your full name"
                  data-testid="lead-popup-parent-name"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Child's Name</label>
                <input
                  type="text"
                  value={form.childName}
                  onChange={(e) => setForm({ ...form, childName: e.target.value })}
                  placeholder="Child's full name"
                  data-testid="lead-popup-child-name"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mobile Number *</label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="10-digit number"
                  data-testid="lead-popup-phone"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Preferred Campus</label>
                <Select onValueChange={(v) => {
                    const c = campusList.find((x) => x.name === v);
                    setForm({ ...form, campus: v, campusId: c?.id || "", grade: "" });
                  }}>
                  <SelectTrigger className="border-gray-200 text-sm" data-testid="lead-popup-campus">
                    <SelectValue placeholder="Select campus" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {campusNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Grade Applying For</label>
                <Select
                  disabled={!form.campus}
                  onValueChange={(v) => setForm({ ...form, grade: v })}
                >
                  <SelectTrigger className="border-gray-200 text-sm" data-testid="lead-popup-grade" disabled={!form.campus}>
                    <SelectValue placeholder={form.campus ? "Select grade" : "Select campus first"} />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {gradeList.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-popup-submit"
                className="w-full bg-[#9B1522] hover:bg-[#87111E] text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 mt-1"
              >
                {loading ? <Loader size={16} className="animate-spin" /> : null}
                Book Free Counselling Session
              </button>
              <p className="text-center text-xs text-gray-400">No spam. We'll only call once to assist you.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadPopup;